import { type FocusEvent, type KeyboardEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { daysLabel, hoursLabel } from "@/lib/effort";
import { formatMonth } from "@/lib/format";
import { i18n } from "@/lib/i18n";
import { isKeyboardFocus } from "@/lib/input-modality";
import { STRIPES, stripeTile } from "@/lib/stripes";
import { cn } from "@/lib/utils";

import {
  labelAnchor,
  monthChart,
  type MonthChartGeometry,
  type MonthChartItem,
  type MonthSegment,
} from "./month-geometry";

/** The bar's band in the SVG, in px: the budget's label above it, the line through it. */
const BAR_TOP = 18;
const BAR_HEIGHT = 28;
const LINE_OVERHANG = 4;
const LABEL_BASELINE = 11;
/** The bar's top without a budget: no label above it, only room for the focus ring. */
const BARE_TOP = 4;

/**
 * The keyboard's focus ring around a segment, in px out from its edge: a gap
 * in the card's colour, then a ring in ink, as the dashboard's focus rings
 * keep their offset. Ink shows on both blues and over the stripes.
 */
const FOCUS_GAP = 2;
const FOCUS_RING = 4;

type Tone = MonthSegment["tone"];

/** Each tone's fill, and the stripes over it: light on the main blue, ink on the light blue. */
const TONE = {
  done: { fill: "fill-chart-1", stripes: "stroke-primary-foreground" },
  planned: { fill: "fill-chart-2", stripes: "stroke-foreground" },
} as const satisfies Record<Tone, { fill: string; stripes: string }>;

interface MonthChartProps {
  /** The month on show, "2026-11", for the chart's description. */
  month: string;
  /** The month's budget, in hours; null for a viewer who does not see it (an admin). */
  budgetHours: number | null;
  /** The month's items, done first and then by rank, as the server sends them. */
  items: readonly MonthChartItem[];
  /** Opens an item's details: each segment is then a button. */
  onOpenItem?: (id: string) => void;
}

/**
 * The month's work against its budget, in one horizontal bar: each of the
 * month's items a segment as wide as its hours, the done ones in De Ceuster
 * blauw and the planned and in-progress ones in dc-blauw-licht, with 2 px
 * white gaps between them, over a light grey track. The axis runs to the
 * budget, or to the total when the month is over it, and a 3 px dashed ink
 * line marks the budget, labelled in the muted colour. What lies past the
 * line is striped over its own fill, the house style's mark for "vraagt
 * aandacht" (`@/lib/stripes`, as in the Bezettingsgraad app), and the legend
 * then names the stripes. Square, no border, per the De Ceuster figure style.
 * Without a budget (an admin's view) the bar shows the month's progress
 * alone: its axis runs to the month's total, with no line and no stripes.
 * An admin's items carry no hours: each segment is as wide as its item's
 * share of the month, and nothing on the chart names hours or days, its
 * name counting the items instead.
 *
 * Hovering or tabbing to a segment shows its item's title and hours (the
 * title alone without hours), and a click, Enter or Space opens the item's
 * details. A segment the keyboard reached gets a focus ring drawn over
 * everything else (`<FocusRing>`), so the gaps, the stripes and the budget
 * line never hide it. A touch screen shows no tooltips, so the figure's name
 * sums the month up, and the table lists every item. Dependency-free SVG,
 * sized in percent of the bar, like `<CallsChart>`.
 */
export function MonthChart({ month, budgetHours, items, onOpenItem }: MonthChartProps) {
  const { t } = useTranslation();
  const patternBase = patternId(useId());
  const patternOf = (tone: Tone) => `${patternBase}-${tone}`;
  // The segment the keyboard is on; a mouse click focuses one too, without a ring.
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const chart = monthChart(budgetHours, items);
  const focused = chart.segments.find((segment) => segment.id === focusedId);
  const top = chart.budgetX === null ? BARE_TOP : BAR_TOP;

  return (
    <figure aria-label={chartLabel(month, budgetHours, items, chart)}>
      <div className="text-foreground mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="bg-chart-1 inline-block h-2.5 w-2.5" />
          {t("roadmap.month.legendDone")}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="bg-chart-2 inline-block h-2.5 w-2.5" />
          {t("roadmap.month.legendPlanned")}
        </span>
        {chart.overBudget && (
          <span className="flex items-center gap-1.5" data-legend="over">
            <span aria-hidden="true" className="flex gap-0.5">
              {chart.overTones.map((tone) => (
                <StripeSwatch key={tone} tone={tone} className="h-2.5 w-2.5" />
              ))}
            </span>
            {t("roadmap.month.legendOver")}
          </span>
        )}
      </div>
      <svg
        className={cn("w-full overflow-visible", chart.budgetX === null ? "h-[36px]" : "h-[52px]")}
      >
        {chart.overBudget && (
          <defs>
            {chart.overTones.map((tone) => (
              <StripePattern key={tone} id={patternOf(tone)} tone={tone} />
            ))}
          </defs>
        )}
        {chart.budgetX !== null && budgetHours !== null && (
          <text
            x={`${chart.budgetX}%`}
            y={LABEL_BASELINE}
            textAnchor={labelAnchor(chart.budgetX)}
            className="fill-muted text-xs"
            aria-hidden="true"
          >
            {t("roadmap.month.budgetLine", { effort: daysLabel(budgetHours) })}
          </text>
        )}
        <rect
          x="0"
          y={top}
          width="100%"
          height={BAR_HEIGHT}
          className="fill-chart-3"
          aria-hidden="true"
        />
        {chart.segments.map((segment) => (
          <Segment
            key={segment.id}
            segment={segment}
            top={top}
            onOpenItem={onOpenItem}
            onFocusChange={setFocusedId}
          />
        ))}
        {chart.segments.map(
          (segment) =>
            segment.over && (
              <rect
                key={segment.id}
                x={`${segment.over.x}%`}
                y={top}
                width={`${segment.over.width}%`}
                height={BAR_HEIGHT}
                fill={`url(#${patternOf(segment.tone)})`}
                className="pointer-events-none"
                data-over={segment.tone}
                aria-hidden="true"
              />
            ),
        )}
        {chart.gaps.map((x) => (
          <line
            key={x}
            x1={`${x}%`}
            x2={`${x}%`}
            y1={top}
            y2={top + BAR_HEIGHT}
            strokeWidth={2}
            className="stroke-card pointer-events-none"
            aria-hidden="true"
          />
        ))}
        {chart.budgetX !== null && (
          <line
            x1={`${chart.budgetX}%`}
            x2={`${chart.budgetX}%`}
            y1={top - LINE_OVERHANG}
            y2={top + BAR_HEIGHT + LINE_OVERHANG}
            strokeWidth={3}
            strokeDasharray="6 4"
            className="stroke-foreground pointer-events-none"
            data-budget-line=""
            aria-hidden="true"
          />
        )}
        {focused && <FocusRing x={focused.x} width={focused.width} top={top} />}
      </svg>
    </figure>
  );
}

/**
 * The chart's name, which sums the month up for a screen reader and a touch
 * screen: for a viewer who sees the budget, the work done and still planned
 * in days against it; for an admin, who sees neither budget nor hours, how
 * many items are done and how many planned or in progress. Those counts
 * come from the month's items, not the bar's segments, so an item that takes
 * no room on the bar (a share of 0) is still counted, as the table lists it.
 */
function chartLabel(
  month: string,
  budgetHours: number | null,
  items: readonly MonthChartItem[],
  chart: MonthChartGeometry,
): string {
  if (budgetHours === null || !chart.byHours) {
    const doneCount = items.filter((item) => item.status === "done").length;
    return i18n.t("roadmap.month.chartProgress", {
      month: formatMonth(month),
      done: i18n.t("roadmap.month.chartDone", { count: doneCount }),
      planned: i18n.t("roadmap.month.chartPlanned", { count: items.length - doneCount }),
    });
  }
  const doneHours = chart.segments
    .filter((segment) => segment.tone === "done")
    .reduce((sum, segment) => sum + (segment.hours ?? 0), 0);
  return i18n.t("roadmap.month.chart", {
    month: formatMonth(month),
    done: daysLabel(doneHours),
    planned: daysLabel(chart.total - doneHours),
    budget: daysLabel(budgetHours),
  });
}

/** An id for stripes' patterns, without the characters a `url(#…)` would trip on. */
function patternId(reactId: string): string {
  return `month-stripes-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

/** The stripes over a tone, as a pattern of 5 px tiles: light on the main blue, ink on the light blue. */
function StripePattern({ id, tone }: { id: string; tone: Tone }) {
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width={STRIPES.period} height={STRIPES.period}>
      <path
        d={stripeTile()}
        strokeWidth={STRIPES.width}
        strokeLinecap="square"
        fill="none"
        className={TONE[tone].stripes}
      />
    </pattern>
  );
}

interface StripeSwatchProps {
  /** The fill under the stripes: the main blue of done work, or the light blue of the rest. */
  tone: Tone;
  /** Its size, such as `h-2.5 w-2.5`. */
  className?: string;
}

/**
 * A small square in a tone with the stripes over it, as the bar draws what
 * lies past the budget: the legend's mark for "Boven budget", and the mark
 * next to that figure in the month block. Its own pattern, so it stands
 * anywhere on the page.
 */
export function StripeSwatch({ tone, className }: StripeSwatchProps) {
  const id = patternId(useId());
  return (
    <svg aria-hidden="true" className={cn("inline-block shrink-0", className)} data-tone={tone}>
      <defs>
        <StripePattern id={id} tone={tone} />
      </defs>
      <rect width="100%" height="100%" className={TONE[tone].fill} />
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

interface SegmentProps {
  segment: MonthSegment;
  /** The bar's top in the SVG, in px. */
  top: number;
  onOpenItem?: (id: string) => void;
  /** The keyboard reached this segment (its id), or left it (null): the chart rings it. */
  onFocusChange: (id: string | null) => void;
}

/**
 * One item's segment, focusable, with its title and hours in a tooltip, the
 * title alone on a bar without hours (an admin's). With `onOpenItem` it is a
 * button: a click, Enter or Space opens the item. Its focus ring is the
 * chart's `<FocusRing>`, drawn over the gaps and stripes that would cover a
 * stroke of its own.
 */
function Segment({ segment, top, onOpenItem, onFocusChange }: SegmentProps) {
  const { t } = useTranslation();
  const hours = segment.hours === null ? null : hoursLabel(segment.hours);
  const open = onOpenItem && (() => onOpenItem(segment.id));
  const onKeyDown = (event: KeyboardEvent<SVGRectElement>) => {
    if (open && (event.key === "Enter" || event.key === " ")) {
      // Space would scroll the page.
      event.preventDefault();
      open();
    }
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <rect
          x={`${segment.x}%`}
          y={top}
          width={`${segment.width}%`}
          height={BAR_HEIGHT}
          tabIndex={0}
          role={open ? "button" : "img"}
          aria-label={
            hours === null
              ? segment.title
              : t("roadmap.month.segment", { title: segment.title, hours })
          }
          data-tone={segment.tone}
          onClick={open}
          onKeyDown={open && onKeyDown}
          // Ringed only when the keyboard brought the focus, as `:focus-visible` would.
          onFocus={(event: FocusEvent<SVGRectElement>) =>
            onFocusChange(isKeyboardFocus(event.currentTarget) ? segment.id : null)
          }
          onBlur={() => onFocusChange(null)}
          className={cn("outline-none", TONE[segment.tone].fill, open && "cursor-pointer")}
        />
      </TooltipTrigger>
      <TooltipContent>
        <span className="block font-semibold">{segment.title}</span>
        {hours !== null && <span className="block font-normal">{hours}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

interface FocusRingProps {
  /** The focused segment's left edge and width, in % of the bar. */
  x: number;
  width: number;
  /** The bar's top in the SVG, in px. */
  top?: number;
}

/**
 * The keyboard's focus ring around a segment, outside it: a 2 px gap in the
 * card's colour, then a 2 px ring in ink, the offset ring of the dashboard's
 * other controls. Drawn last, so the gaps between segments, the stripes and
 * the budget line never cover it. A nested SVG at the segment's box lets the
 * ring sit a few px outside an edge given in percent.
 */
export function FocusRing({ x, width, top = BAR_TOP }: FocusRingProps) {
  return (
    <svg
      x={`${x}%`}
      y={top}
      width={`${width}%`}
      height={BAR_HEIGHT}
      className="pointer-events-none overflow-visible"
      data-focus-ring=""
      aria-hidden="true"
    >
      {/* The ink first, as wide as ring and gap together, then the gap over its inner half. */}
      <RingBand reach={FOCUS_RING} className="fill-foreground" />
      <RingBand reach={FOCUS_GAP} className="fill-card" />
    </svg>
  );
}

/** A band `reach` px wide around the box of its `<svg>`, in four rects. */
function RingBand({ reach, className }: { reach: number; className: string }) {
  const side = BAR_HEIGHT + 2 * reach;
  return (
    <g className={className}>
      <rect x={-reach} y={-reach} width={reach} height={side} />
      <rect x="0" y={-reach} width="100%" height={reach} />
      <rect x="0" y={BAR_HEIGHT} width="100%" height={reach} />
      {/* The right side, from the box's right edge outwards. */}
      <svg x="100%" className="overflow-visible">
        <rect x="0" y={-reach} width={reach} height={side} />
      </svg>
    </g>
  );
}
